import type { IANATimezones, RequestForDocumentTaskData } from '@gcv/shared';
import { TaskCategory, TaskStatus } from '@gcv/shared';

import palette from 'ui/theme/palette';
import { DateTimeHelpers } from 'util/dateTime.util';
import { removeUuid } from 'util/format.util';
import type { TaskDetails, TaskDrawerField } from '../../tasks/tasks.model';
import { getTaskStatusLabel } from './get-task-status-label';

/** Fields shown in the drawer for a CRB-facing (external) task. */
export const getExternalTaskDrawerFields = (
  task: TaskDetails | null,
  timezone: IANATimezones
): Record<string, TaskDrawerField> => ({
  status: {
    label: 'Status',
    value: getTaskStatusLabel(task),
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33,
    color: task?.status === TaskStatus.COMPLETE ? palette.primary.main : undefined
  },
  dueDate: {
    label: 'Due Date',
    value: DateTimeHelpers.formatISOToTableDateString(task?.dueDate, timezone),
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  createdDate: {
    label: 'Sent On',
    value: DateTimeHelpers.formatISOToTableDateString(task?.createdDate, timezone),
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  requirementName: {
    label: 'Requirement',
    value: (task?.data as RequestForDocumentTaskData)?.requirement_name ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  account: {
    label: 'Account',
    value: task?.account ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 100
  },
  assignee: {
    label: 'Assignee',
    value: task?.assignee ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  completedBy: {
    label: 'Completed By',
    value: task?.completedBy ?? '--',
    state: [TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  completedOn: {
    label: 'Completed On',
    value: DateTimeHelpers.formatISOToTableDateString(task?.completedDate, timezone),
    state: [TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  description: {
    label: 'Task Description',
    value: task ? removeUuid(task.description) : '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 100
  },
  lastReminder: {
    label: 'Last Reminder Sent On',
    value: DateTimeHelpers.formatISOToTableDateString(task?.lastReminderDate, timezone),
    state: [TaskStatus.OPEN],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 50
  },
  nextReminder: {
    label: 'Next Reminder Scheduled For',
    value: DateTimeHelpers.formatISOToTableDateString(task?.nextReminderDate, timezone),
    state: [TaskStatus.OPEN],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 50
  },
  createdBy: {
    label: 'Created By',
    value: task?.createdBy ?? '--',
    state: [TaskStatus.OPEN, TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 33
  },
  completedNote: {
    label: 'Completed Note',
    value: task?.completedNote?.length ? task.completedNote : '--',
    state: [TaskStatus.COMPLETE],
    type: [TaskCategory.USER, TaskCategory.SYSTEM],
    width: 100
  }
});
